import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { getConsent, setConsent, registerPreferencesOpener } from "@/lib/consent";

const K = { fontFamily: "Kanit, Arial, sans-serif" };
const S = { fontFamily: "'Noto Serif', Georgia, serif" };
const btn = "text-[11px] tracking-[0.2em] uppercase font-semibold px-4 py-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]";

// Lightweight consent banner + preferences dialog. Analytics (GA4) is optional and off until accepted.
export default function CookieConsent() {
  const { pathname } = useLocation();
  const [decided, setDecided] = useState(() => Boolean(getConsent()));
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [analytics, setAnalytics] = useState(() => Boolean(getConsent()?.analytics));

  const bannerRef = React.useRef(null);
  useEffect(() => { registerPreferencesOpener(() => { setAnalytics(Boolean(getConsent()?.analytics)); setPrefsOpen(true); }); }, []);
  // Expose the banner height so the chat launcher can sit above it on small screens (see SpainMoveAssistant launcher).
  useEffect(() => {
    const root = document.documentElement;
    if (decided || !bannerRef.current) { root.style.removeProperty("--de-consent-offset"); return; }
    const apply = () => root.style.setProperty("--de-consent-offset", `${bannerRef.current?.offsetHeight || 0}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(bannerRef.current);
    return () => { ro.disconnect(); root.style.removeProperty("--de-consent-offset"); };
  }, [decided]);

  const decide = (value) => { setConsent(value); setAnalytics(value); setDecided(true); setPrefsOpen(false); };
  if (pathname.startsWith("/admin")) return null;

  return (
    <>
      {!decided && (
        <aside ref={bannerRef} data-testid="cookie-banner" role="region" aria-label="Cookie preferences" className="fixed z-[40] left-4 right-4 bottom-4 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-[440px] bg-[#F5EFE7] text-[#451C25] border border-[#451C25]/15 shadow-2xl shadow-[#451C25]/25 p-5" style={{ marginBottom: "env(safe-area-inset-bottom)" }}>
          <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#897756]" style={K}>Cookies</p>
          <p className="mt-2 text-[14px] leading-relaxed text-[#6B4954]" style={S}>
            This site uses essential storage to work. With your permission, we also use Google Analytics to understand how the site is used. Analytics stays off until you accept. <Link to="/privacy" className="underline underline-offset-4 decoration-[#B3A17D] text-[#451C25]">Privacy Policy</Link>
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" data-testid="consent-accept-all" onClick={() => decide(true)} className={`${btn} bg-[#451C25] text-[#F5EFE7] hover:bg-[#2B1017]`} style={K}>Accept all</button>
            <button type="button" data-testid="consent-reject" onClick={() => decide(false)} className={`${btn} border border-[#451C25]/30 text-[#451C25] hover:border-[#451C25]`} style={K}>Reject non-essential</button>
            <button type="button" data-testid="consent-manage" onClick={() => setPrefsOpen(true)} className="text-[12px] underline underline-offset-4 decoration-[#B3A17D] text-[#451C25] px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]" style={{ ...K, fontWeight: 500 }}>Manage preferences</button>
          </div>
        </aside>
      )}

      <DialogPrimitive.Root open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[85] bg-[#2B1017]/50" />
          <DialogPrimitive.Content data-testid="consent-preferences" className="fixed z-[90] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-[520px] max-h-[calc(100vh-2rem)] overflow-y-auto bg-[#F5EFE7] text-[#451C25] border border-[#451C25]/10 shadow-2xl p-6 sm:p-8 focus:outline-none">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#897756]" style={K}>Cookie preferences</p>
                <DialogPrimitive.Title className="mt-1.5 text-2xl leading-tight" style={{ ...K, fontWeight: 600 }}>Choose what we may measure.</DialogPrimitive.Title>
                <DialogPrimitive.Description className="mt-2 text-[14px] leading-relaxed text-[#6B4954]" style={S}>Your choice is stored in your browser and can be changed at any time from the footer.</DialogPrimitive.Description>
              </div>
              <DialogPrimitive.Close aria-label="Close" data-testid="consent-close" className="shrink-0 p-1.5 text-[#451C25]/70 hover:text-[#451C25] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B3A17D]"><X size={18} /></DialogPrimitive.Close>
            </div>

            <ul className="mt-6 border-t border-[#451C25]/15">
              <li className="py-4 border-b border-[#451C25]/15 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[15px]" style={{ ...K, fontWeight: 500 }}>Essential</p>
                  <p className="mt-1 text-[13px] text-[#6B4954] leading-relaxed" style={S}>Local storage that remembers your route selection, chat session and this consent choice. No third-party cookies.</p>
                </div>
                <span className="text-[10px] tracking-[0.2em] uppercase text-[#897756] mt-1" style={K}>Always on</span>
              </li>
              <li className="py-4 border-b border-[#451C25]/15 flex items-start justify-between gap-4">
                <div>
                  <p className="text-[15px]" style={{ ...K, fontWeight: 500 }}>Analytics (Google Analytics 4)</p>
                  <p className="mt-1 text-[13px] text-[#6B4954] leading-relaxed" style={S}>Aggregated page views and interactions (for example which route or guide was chosen). We do not send names, emails or chat text to Google.</p>
                </div>
                <label className="inline-flex items-center gap-2 mt-1 cursor-pointer">
                  <input data-testid="consent-analytics-toggle" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="h-4 w-4 accent-[#451C25]" />
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#451C25]" style={K}>{analytics ? "On" : "Off"}</span>
                </label>
              </li>
            </ul>

            <div className="mt-6 flex flex-wrap gap-2">
              <button type="button" data-testid="consent-save" onClick={() => decide(analytics)} className={`${btn} bg-[#451C25] text-[#F5EFE7] hover:bg-[#2B1017]`} style={K}>Save preferences</button>
              <button type="button" data-testid="consent-prefs-accept-all" onClick={() => decide(true)} className={`${btn} border border-[#451C25]/30 text-[#451C25] hover:border-[#451C25]`} style={K}>Accept all</button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  );
}
